import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  useTSQValidateToken,
  useTSMCompleteRegistration,
} from "../../hooks/useTSRegistration";
import {
  validatePassword,
  validatePasswordMatch,
  getPasswordStrengthDisplay,
} from "../../utils/passwordUtils";
import { UserRegistrationComplete } from "../../types/registrationTypes";

// Validation schema
const registrationSchema = yup.object({
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long"),
  confirm_password: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password")], "Passwords must match"),
  acceptTerms: yup
    .boolean()
    .oneOf([true], "You must accept the terms and conditions"),
});

interface RegistrationFormData extends UserRegistrationComplete {
  acceptTerms: boolean;
}

export const PageCompleteRegistrationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // State
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);

  // Token validation query
  const {
    data: tokenInfo,
    isLoading: tokenLoading,
    error: tokenError,
  } = useTSQValidateToken(token || "", !!token);

  // Registration completion mutation
  const completRegistrationMutation = useTSMCompleteRegistration();

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<RegistrationFormData>({
    resolver: yupResolver(registrationSchema) as any,
    defaultValues: {
      password: "",
      confirm_password: "",
      acceptTerms: false,
    },
  });

  const watchPassword = watch("password");

  // Password strength validation
  const passwordValidation = validatePassword(watchPassword);
  const strengthDisplay = getPasswordStrengthDisplay(passwordValidation.strength);

  // Success countdown effect
  useEffect(() => {
    if (success && redirectCountdown > 0) {
      const timer = setTimeout(() => {
        setRedirectCountdown(redirectCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (success && redirectCountdown === 0) {
      navigate("/login");
    }
  }, [success, redirectCountdown, navigate]);

  // Form submission
  const onSubmit = async (data: RegistrationFormData) => {
    if (!token) {
      setError("Registration token is missing");
      return;
    }

    try {
      setError(null);

      const registrationData: UserRegistrationComplete = {
        password: data.password,
        confirm_password: data.confirm_password,
      };

      await completRegistrationMutation.mutateAsync({
        token,
        registrationData,
      });

      setSuccess(true);
    } catch (err: any) {
      console.error("Registration failed:", err);
      setError(err.message || "Registration failed. Please try again.");
    }
  };

  // Loading state
  if (tokenLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 400, width: "100%" }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="h6">Validating invitation...</Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we verify your registration link.
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Invalid token state
  if (tokenError || !tokenInfo?.valid) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, width: "100%" }}>
          <Stack alignItems="center" spacing={3}>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main" }} />
            <Typography variant="h5" align="center">
              Invalid Invitation
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary">
              {tokenInfo?.error || 
               "This invitation link is invalid or has expired. Please contact an administrator for a new invitation."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              fullWidth
            >
              Go to Login
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 500, width: "100%" }}>
          <Stack alignItems="center" spacing={3}>
            <CheckCircleIcon sx={{ fontSize: 64, color: "success.main" }} />
            <Typography variant="h5" align="center">
              Registration Complete!
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary">
              Your account has been created successfully. You can now log in with your new credentials.
            </Typography>
            <Card sx={{ width: "100%", backgroundColor: "success.50" }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to login page in {redirectCountdown} seconds...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(5 - redirectCountdown) * 20}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              fullWidth
            >
              Go to Login Now
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Registration form
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 4,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 500, width: "100%" }}>
        <Stack spacing={3}>
          {/* Header */}
          <Box textAlign="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Complete Registration
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome! Complete your account setup below.
            </Typography>
          </Box>

          {/* User Info */}
          <Card sx={{ backgroundColor: "primary.50" }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Account Details:
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {tokenInfo?.email}
              </Typography>
              {tokenInfo?.first_name && (
                <Typography variant="body2">
                  <strong>Name:</strong> {tokenInfo.first_name} {tokenInfo.last_name}
                </Typography>
              )}
              {tokenInfo?.expires_at && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Expires:</strong> {new Date(tokenInfo.expires_at).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              {/* Password Field */}
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type="password"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message || ""}
                  />
                )}
              />

              {/* Password Strength Indicator */}
              {watchPassword && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="body2">Password Strength:</Typography>
                    <Chip
                      label={strengthDisplay.label}
                      size="small"
                      sx={{
                        backgroundColor: strengthDisplay.color,
                        color: "white",
                      }}
                    />
                  </Stack>
                  {passwordValidation.errors.length > 0 && (
                    <Box>
                      {passwordValidation.errors.map((error, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          color="error"
                          display="block"
                        >
                          • {error}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Confirm Password Field */}
              <Controller
                name="confirm_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message || ""}
                  />
                )}
              />

              {/* Terms Acceptance */}
              <Controller
                name="acceptTerms"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        color="primary"
                      />
                    }
                    label="I accept the terms and conditions"
                  />
                )}
              />
              {errors.acceptTerms && (
                <Typography variant="caption" color="error">
                  {errors.acceptTerms.message}
                </Typography>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting || completRegistrationMutation.isPending}
              >
                {isSubmitting || completRegistrationMutation.isPending
                  ? "Creating Account..."
                  : "Complete Registration"}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Box>
  );
};